/**
 * MockHome - consumer-facing AI Mock Interviews landing.
 *
 * Sprint A: admin-guarded so we can dogfood internally before opening
 * to free / premium / star tiers. Renders:
 *   - filter chips (language, type, difficulty)
 *   - template grid (practice library)
 *   - "Start mock" flow inline on each card
 *
 * Designed mobile-first; the grid collapses to a single column < 640px.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Clock, Sparkles, Sliders, Loader2, History as HistoryIcon, CheckCircle2, AlertCircle,
  Video, ClipboardList, Lock, ShieldCheck, Eye, Search, X, Wand2, Code as CodeIcon, PenTool, Calculator, Shuffle, FileText as FileTextIcon, Info,
  ChevronDown, LayoutGrid, Crown,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  cleanTemplateTitle,
  sanitizeInterviewCopy,
  getMockQuota,
  listMockLibrary,
  startMock,
  startCustomMock,
  getTemplate,
  fetchPersonas,
  DEFAULT_PERSONA_ID,
  type MockLibraryTemplate,
  type MockQuota,
  type InterviewTemplate,
  type Persona,
  type TemplateDomain,
  type TemplateType,
  type MockStartCustomPayload,
  type InterviewQuestion,
} from '../../api/interview';
import {
  formatQuestionsAsCustomizeTopic,
  pickSampleTaskSteps,
} from '../../lib/interviewPoolTasks';
import TemplateDetailModal from '../../components/Interview/TemplateDetailModal';
import PersonaVoicePlayButton from '../../components/Interview/PersonaVoicePlayButton';
import RecruiterExplorerGate from '../../components/RecruiterExplorerGate';
import AuthModal from '../../components/Auth/AuthModal';
import RateLimitNotification from '../../components/RateLimitNotification';
import { useAuthModal } from '../../hooks/useAuthModal';
import { useUserProfile } from '../../contexts/UserProfileContext';
import type { RateLimitInfo } from '../../utils/errorHandler';
import { setCheckoutReturn } from '../../lib/checkout';


type LangFilter = 'all' | 'de' | 'en';
type TypeFilter = 'all' | 'behavioral' | 'coding' | 'system_design' | 'scenario';
type DomainFilter = 'all' | TemplateDomain;

/** Visible roster only — retired ids (e.g. mira on old templates) → Lara. */
function resolveVisiblePersonaId(
  personaId: string | null | undefined,
  personas: Persona[],
): string {
  const allowed = new Set(personas.map((p) => p.id));
  if (personaId && allowed.has(personaId as Persona['id'])) return personaId;
  if (allowed.has(DEFAULT_PERSONA_ID)) return DEFAULT_PERSONA_ID;
  return personas[0]?.id ?? DEFAULT_PERSONA_ID;
}

/** Library card → modal shape (questions loaded on demand). */
function libraryAsInterviewTemplate(t: MockLibraryTemplate): InterviewTemplate {
  return {
    id: t.id,
    company_id: null,
    title: t.title,
    description: t.description,
    type: (t.type || 'behavioral') as InterviewTemplate['type'],
    difficulty: (t.difficulty as InterviewTemplate['difficulty']) || null,
    domain: t.domain,
    duration_minutes: t.duration_minutes,
    language: t.language,
    family_key: t.family_key,
    enable_coding_panel: t.enable_coding_panel,
    enable_whiteboard_panel: t.enable_whiteboard_panel,
    enable_numbers_panel: t.enable_numbers_panel,
    enable_notepad_panel: t.enable_notepad_panel,
    enable_screen_share: t.enable_screen_share,
    available_for_practice: true,
    available_for_recruiter: false,
    evaluation_criteria: [],
    is_public: true,
    is_active: true,
    persona_id: (t.persona_id || DEFAULT_PERSONA_ID) as InterviewTemplate['persona_id'],
    created_by: null,
    created_at: 0,
    updated_at: 0,
    questions: [],
    sampling_strategy: t.sampling_strategy ?? (t.is_pool ? { total: 1 } : null),
    steps_per_session: t.steps_per_session ?? null,
  };
}

/**
 * Static library shown to logged-out visitors. Mirrors the real
 * library in counts, titles, and descriptions so the guest skeleton
 * reads as the actual product (not a stripped-down preview).
 *
 * Each template appears as DE+EN with a shared `family_key` so the
 * card's EN/DE toggle works the same as for signed-in users.
 */
const _gp = (
  baseId: string,
  domain: TemplateDomain,
  type: TemplateType,
  duration: number,
  steps: number,
  panels: { code?: boolean; wb?: boolean; nums?: boolean; np?: boolean },
  en: { title: string; desc: string },
  de: { title: string; desc: string },
  difficulty: 'student' | 'junior' | 'mid' | 'senior' = 'mid',
): MockLibraryTemplate[] => {
  const common = {
    type, difficulty, domain, duration_minutes: duration, persona_id: DEFAULT_PERSONA_ID,
    enable_coding_panel: !!panels.code, enable_whiteboard_panel: !!panels.wb,
    enable_numbers_panel: !!panels.nums, enable_notepad_panel: !!panels.np,
    enable_screen_share: true, is_pool: true, pool_side: 'practice' as const,
    steps_per_session: steps, family_key: `guest-${baseId}`,
  };
  return [
    { ...common, id: `guest-${baseId}-en`, language: 'en', title: en.title, description: en.desc },
    { ...common, id: `guest-${baseId}-de`, language: 'de', title: de.title, description: de.desc },
  ];
};

const GUEST_PREVIEW_TEMPLATES: MockLibraryTemplate[] = [
  // General (3)
  ..._gp('behav-general', 'general', 'behavioral', 45, 5, {},
    { title: 'Behavioral General', desc: 'General behavioral interview: impact, conflict, mistakes, prioritisation in STAR format. Works for any role.' },
    { title: 'Behavioral General', desc: 'Allgemeines Behavioral-Interview: Impact, Konflikt, Fehlerkultur und Priorisierung im STAR-Format. Für alle Rollen geeignet.' }),
  ..._gp('comms-leadership', 'general', 'behavioral', 45, 5, {},
    { title: 'Communication & Leadership', desc: 'Comms and leadership screen: feedback, hard conversations, alignment, exec updates. Voice-only.' },
    { title: 'Communication & Leadership', desc: 'Kommunikations- und Leadership-Screen: Feedback, schwierige Gespräche, Alignment und Executive-Updates. Voice-only.' }),
  ..._gp('founders-associate', 'general', 'behavioral', 45, 5, {},
    { title: 'Founders Associate Screen', desc: 'Founders associate screen: ownership, prioritisation, communication, analytical thinking in a startup context.' },
    { title: 'Founders Associate Screen', desc: 'Founders-Associate-Screen: Ownership, Priorisierung, Kommunikation und analytisches Denken in einem Startup-Kontext.' }),
  // Tech (9)
  ..._gp('ai-engineer', 'tech', 'coding', 45, 3, { code: true, wb: true },
    { title: 'AI Engineer', desc: 'AI engineer screen: RAG, tool use, evals and prod operation. Architecture on the whiteboard, code in the live editor.' },
    { title: 'AI Engineer', desc: 'AI-Engineer-Screen: RAG, Tool Use, Evals und Production-Betrieb. Architektur auf dem Whiteboard, Implementation im Live-Editor.' }),
  ..._gp('api-design', 'tech', 'system_design', 45, 3, { code: true, wb: true },
    { title: 'API Design', desc: 'API design screen: model interfaces, error cases and trade-offs, optional with coding.' },
    { title: 'API Design', desc: 'API-Design-Screen: Schnittstellen modellieren, Fehlerfälle und Trade-offs erklären, optional mit Coding.' }),
  ..._gp('backend-tech', 'tech', 'coding', 45, 3, { code: true, wb: true },
    { title: 'Backend Tech Screen', desc: 'Backend technical screen: APIs, data model, scaling and hands-on coding in the live editor.' },
    { title: 'Backend Tech Screen', desc: 'Backend-Technical-Screen: APIs, Datenmodell, Skalierung und praktisches Coding im Live-Editor.' }),
  ..._gp('system-design', 'tech', 'system_design', 60, 5, { wb: true },
    { title: 'System Design', desc: 'System design interview: requirements, estimation, architecture, deep dive and scaling. Whiteboard for diagrams and trade-offs.' },
    { title: 'System Design', desc: 'System-Design-Interview: Anforderungen, Schätzung, Architektur, Deep Dive und Skalierung. Whiteboard für Diagramme und Trade-offs.' }, 'senior'),
  ..._gp('dsa', 'tech', 'coding', 45, 5, { code: true },
    { title: 'Data Structures & Algorithms', desc: 'Technical coding interview: structured algorithm task with the live editor: from understanding via approach to code and complexity.' },
    { title: 'Data Structures & Algorithms', desc: 'Technical Coding-Interview: strukturierte Algorithmen-Aufgabe mit Live-Editor: von Problemverständnis über Ansatz bis Code und Komplexität.' }),
  ..._gp('fde', 'tech', 'coding', 45, 3, { code: true, wb: true },
    { title: 'Forward Deployment Engineer', desc: 'FDE screen: integrate ML at the customer, debug on-site and ship; VPC, latency and stakeholder-grade runbooks.' },
    { title: 'Forward Deployment Engineer', desc: 'Forward-Deployment-Engineer-Screen: ML beim Kunden integrieren, vor Ort debuggen und ausrollen, VPC, Latenz und stakeholder-taugliche Runbooks.' }),
  ..._gp('frontend-tech', 'tech', 'coding', 45, 3, { code: true },
    { title: 'Frontend Tech Screen', desc: 'Frontend technical screen: UI architecture, state, performance and hands-on coding in the live editor.' },
    { title: 'Frontend Tech Screen', desc: 'Frontend-Technical-Screen: UI-Architektur, State, Performance und praktisches Coding im Live-Editor.' }),
  ..._gp('oo-coding', 'tech', 'coding', 45, 3, { code: true },
    { title: 'Object-Oriented Coding', desc: 'OO coding interview: clean object design and implementation of a small component in the live editor.' },
    { title: 'Object-Oriented Coding', desc: 'OO-Coding-Interview: sauberes Objekt-Design und Implementierung einer kleinen Komponente im Live-Editor.' }),
  ..._gp('oo-design', 'tech', 'system_design', 45, 3, { wb: true },
    { title: 'Object-Oriented Design', desc: 'OO design interview: discuss and sketch classes, responsibilities and extensibility.' },
    { title: 'Object-Oriented Design', desc: 'OO-Design-Interview: Klassen, Verantwortlichkeiten und Erweiterbarkeit diskutieren und skizzieren.' }),
  // Product (3)
  ..._gp('pm-behav', 'product', 'behavioral', 45, 5, {},
    { title: 'PM Behavioral', desc: 'PM behavioral screen: STAR stories on prioritisation, stakeholders, conflicts and tough trade-offs. Voice-only.' },
    { title: 'PM Behavioral', desc: 'PM-Behavioral-Screen: STAR-Stories zu Priorisierung, Stakeholdern, Konflikten und schwierigen Trade-offs. Voice-only.' }),
  ..._gp('pm-case', 'product', 'scenario', 60, 5, { wb: true, nums: true, np: true },
    { title: 'PM Case', desc: 'PM case interview: product sense, metrics, prioritisation and root-cause. Whiteboard for flows and design, numbers for metric math.' },
    { title: 'PM Case', desc: 'PM-Case-Interview: Product Sense, Metriken, Priorisierung und Root-Cause. Whiteboard für Flows und Design, Numbers für Metric-Math.' }),
  ..._gp('pm-screen', 'product', 'behavioral', 45, 5, { wb: true },
    { title: 'PM Screen', desc: 'PM screening: product sense, metrics drill, prioritisation scenario and execution story. Whiteboard for user flows and frameworks.' },
    { title: 'PM Screen', desc: 'PM-Screening: Product Sense, Metriken-Übung, Priorisierungs-Szenario und Execution-Story. Whiteboard für User-Flows und Frameworks.' }),
  // Sales / GTM (4)
  ..._gp('gtm-engineer', 'sales_gtm', 'coding', 45, 3, { code: true, wb: true },
    { title: 'GTM Engineer', desc: 'GTM engineer screen: technical demos, integrations and customer-facing problem solving with code and whiteboard.' },
    { title: 'GTM Engineer', desc: 'GTM-Engineer-Screen: technische Demos, Integrationen und kundennahes Problemlösen mit Code und Whiteboard.' }),
  ..._gp('sales-quant', 'sales_gtm', 'scenario', 45, 5, { nums: true, np: true },
    { title: 'Sales Quant', desc: 'RevOps / Sales Quant screen: pipeline, capacity, forecast and quota math. Concrete scenarios in the numbers pane.' },
    { title: 'Sales Quant', desc: 'RevOps-/Sales-Quant-Screen: Pipeline, Kapazität, Forecast und Quota-Math. Konkrete Szenarien im Numbers-Pane.' }),
  ..._gp('sales-roleplay', 'sales_gtm', 'behavioral', 45, 5, {},
    { title: 'Sales Role-Play', desc: 'Sales role-play: discovery, demo, objection and close. The AI plays the buyer; you run the call as you would live.' },
    { title: 'Sales Role-Play', desc: 'Sales-Role-Play: Discovery, Demo, Einwand und Abschluss. Die KI spielt den Käufer, du führst das Gespräch wie im echten Call.' }),
  ..._gp('sales-screen', 'sales_gtm', 'behavioral', 45, 5, {},
    { title: 'Sales Screen', desc: 'Sales screening: pitch under pressure, discovery, objection handling and motivation for sales. Voice-only first call.' },
    { title: 'Sales Screen', desc: 'Sales-Screening: Pitch unter Druck, Discovery, Einwandbehandlung und Motivation für Vertrieb. Voice-only wie ein echtes Erstgespräch.' }),
  // Consulting (3: CL, IL, market sizing)
  ..._gp('consulting-case-cl', 'consulting', 'scenario', 60, 5, { wb: true, nums: true, np: true },
    { title: 'Consulting Case Candidate Led', desc: 'Candidate-led consulting case: you drive structure, the AI listens and probes. Whiteboard and numbers live.' },
    { title: 'Consulting Case Candidate Led', desc: 'Candidate-Led Consulting Case: Du strukturierst selbst, die KI hoert zu und probt gezielt. Whiteboard und Numbers live.' }, 'senior'),
  ..._gp('consulting-case-il', 'consulting', 'scenario', 60, 5, { wb: true, nums: true, np: true },
    { title: 'Consulting Case Interviewer Led', desc: 'Interviewer-led consulting case: the AI leads, releases data on request and probes your hypotheses. Whiteboard and numbers live.' },
    { title: 'Consulting Case Interviewer Led', desc: 'Interviewer-Led Consulting Case: Die KI fuehrt aktiv, gibt Daten auf Anfrage frei und probt Hypothesen. Whiteboard und Numbers live.' }, 'senior'),
  ..._gp('consulting-ms', 'consulting', 'scenario', 45, 5, { wb: true, nums: true, np: true },
    { title: 'Consulting Market Sizing', desc: 'Market sizing case in consulting style: top-down structure, name assumptions, calculate in numbers, sanity-check, deliver a pointed recommendation.' },
    { title: 'Consulting Market Sizing', desc: 'Market-Sizing-Case im Consulting-Stil: top-down strukturieren, Annahmen benennen, im Numbers-Pane rechnen, sanity-checken und eine pointed Recommendation liefern.' }),
  // Marketing (3)
  ..._gp('mkt-brand', 'marketing', 'behavioral', 45, 5, {},
    { title: 'Marketing Brand', desc: 'Brand and comms screen: positioning, crisis, launch and messaging. Strategic questions mixed with STAR stories, voice-only.' },
    { title: 'Marketing Brand', desc: 'Brand- und Comms-Screen: Positionierung, Krise, Launch und Messaging. Strategische Fragen und STAR-Stories, voice-only.' }),
  ..._gp('mkt-growth', 'marketing', 'scenario', 45, 5, { nums: true, np: true },
    { title: 'Marketing Growth', desc: 'Performance and growth marketing: funnel diagnosis, CAC/LTV, budget prioritisation and campaign post-mortems. Math in the numbers pane.' },
    { title: 'Marketing Growth', desc: 'Performance- und Growth-Marketing-Screen: Funnel-Diagnose, CAC/LTV, Budget-Priorisierung und Kampagnen-Post-Mortems. Rechnungen im Numbers-Pane.' }),
  ..._gp('mkt-manager', 'marketing', 'behavioral', 45, 5, { wb: true },
    { title: 'Marketing Manager', desc: 'Screen for performance, growth and brand marketing: positioning thinking, channel diagnosis, light funnel math and post-mortems. Whiteboard for funnel sketches.' },
    { title: 'Marketing Manager', desc: 'Screening für Performance-, Growth- und Brand-Marketing: Positionierungs-Denken, Channel-Diagnose, leichte Funnel-Mathe und Kampagnen-Post-Mortem. Whiteboard für Funnel-Skizzen.' }),
  // Finance (2)
  ..._gp('finance-dcf', 'finance', 'scenario', 60, 5, { wb: true, nums: true },
    { title: 'Finance DCF', desc: 'Finance technical interview: DCF, multiples, LBO logic and accounting. Explain assumptions and build models live in the numbers pane.' },
    { title: 'Finance DCF', desc: 'Finance-Technical-Interview: DCF, Multiples, LBO-Logik und Accounting. Du erklärst Annahmen und baust Modelle live im Numbers-Pane.' }, 'senior'),
  ..._gp('finance-ib', 'finance', 'scenario', 60, 5, { wb: true, nums: true },
    { title: 'Finance IB', desc: 'IB / PE technical: LBO mechanics, sources & uses, IRR, debt and exit. The AI drills modelling and deal intuition in the numbers pane.' },
    { title: 'Finance IB', desc: 'IB/PE-Technical: LBO-Mechanik, Sources & Uses, IRR, Debt und Exit. Die KI prüft Modell-Denken und Deal-Intuition im Numbers-Pane.' }, 'senior'),
];

function mockQuotaToRateLimitInfo(q: MockQuota): RateLimitInfo {
  const subscription_type =
    q.tier === 'premium' || q.tier === 'star' ? 'explorer_premium' : 'explorer_free';
  return {
    allowed: false,
    global_used: q.used,
    global_limit: q.total,
    feature_used: q.used,
    feature_limit: q.total,
    resets_at: new Date(q.period_end * 1000).toISOString(),
    subscription_type,
    type: 'subscription_limit',
  };
}


interface MockHomeProps {
  /**
   * When true, MockHome is rendered inside the Home2 tab shell. We then
   * drop the standalone "Cevee Simulations [Beta]" branding header (the
   * tab bar already establishes context) and tighten the hero block so
   * the page reads like the other Home2 tabs (Explore / Inspire) instead
   * of a separate landing page. The standalone /mock route still passes
   * embedded=false so deep links land on the full standalone view.
   */
  embedded?: boolean;
}

const MockHome: React.FC<MockHomeProps> = ({ embedded = false }) => {
  const { apiCall, isSignedIn, isLoading: authLoading } = useAuth();
  const { showAuthModal, setShowAuthModal, requireAuth } = useAuthModal();
  const { userProfile } = useUserProfile();
  const navigate = useNavigate();
  const { language: uiLang, toggleLanguage } = useLanguage();
  const isGuest = !authLoading && !isSignedIn;

  const [quota, setQuota] = useState<MockQuota | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [templates, setTemplates] = useState<MockLibraryTemplate[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);

  const [lang, setLang] = useState<LangFilter>('all');
  const [typ, setTyp] = useState<TypeFilter>('all');
  const [domain, setDomain] = useState<DomainFilter>('all');
  const [search, setSearch] = useState('');
  const [showPracticeInfo, setShowPracticeInfo] = useState(false);
  // Cevee template library is collapsed by default so the page leads with
  // "build your own interview"; expand to browse the ready-made formats.
  const [showLibrary, setShowLibrary] = useState(false);

  const [detailTemplate, setDetailTemplate] = useState<InterviewTemplate | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  /** Full templates (with questions) keyed by id — for DE/EN detail toggle. */
  const [loadedTemplateDetails, setLoadedTemplateDetails] = useState<
    Record<string, InterviewTemplate>
  >({});

  // Explicit consent gate: clicking Start on a card opens this modal,
  // which requires an active checkbox tick + final Start click before
  // we actually create the mock session. The user's explicit consent
  // is forwarded to the backend (audit-logged with IP + UA).
  const [consentFor, setConsentFor] = useState<{
    templateId: string;
    language: 'de' | 'en';
    isPool: boolean;
    templateDuration: number;
    enTemplateId?: string;
    deTemplateId?: string;
  } | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  // Candidate-selected career level. Drives system-prompt tone, hints,
  // and rigour for ALL mock sessions (pool + fixed templates). Default
  // = "mid" (most common stage); future iteration can pre-fill from the
  // user profile.
  const [targetDifficulty, setTargetDifficulty] = useState<'student' | 'junior' | 'mid' | 'senior'>('mid');
  // Candidate-selected duration override. Three buckets covering the
  // realistic interview lengths (30 = warm-up, 45 = standard, 60 =
  // extended deep-dive). The system prompt receives this as the
  // effective wall-clock budget and the AI paces accordingly.
  const [targetDuration, setTargetDuration] = useState<30 | 45 | 60>(45);
  const [consentPersonaId, setConsentPersonaId] = useState<string>(DEFAULT_PERSONA_ID);
  // Optional candidate-provided background that gets piped into the AI's
  // system prompt as a "context" block. Lets users practice a specific
  // Optional focus nudge on top of the chosen template (role, tone, emphasis).
  const [customContext, setCustomContext] = useState<string>('');

  // Custom-mock builder (free-form topic + own settings, no template)
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customStarting, setCustomStarting] = useState(false);
  /** Pre-fill custom mock builder from a library template (Customize). */
  const [customPrefill, setCustomPrefill] = useState<{
    topic: string;
    type: MockStartCustomPayload['type'];
    difficulty: MockStartCustomPayload['difficulty'];
    duration_minutes: MockStartCustomPayload['duration_minutes'];
    language: 'de' | 'en';
    persona_id: string;
    enable_coding_panel: boolean;
    enable_whiteboard_panel: boolean;
    enable_numbers_panel: boolean;
    enable_notepad_panel: boolean;
  } | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (isGuest) {
      const filtered = GUEST_PREVIEW_TEMPLATES.filter((t) => {
        if (lang !== 'all' && t.language !== lang) return false;
        if (domain !== 'all' && t.domain !== domain) return false;
        if (typ !== 'all' && t.type !== typ) return false;
        return true;
      });
      setTemplates(filtered);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [q, lib] = await Promise.all([
          getMockQuota(apiCall),
          listMockLibrary(apiCall, {
            language: lang === 'all' ? undefined : lang,
            type: typ === 'all' ? undefined : typ,
            domain: domain === 'all' ? undefined : domain,
          }),
        ]);
        if (cancelled) return;
        setQuota(q.quota);
        setTemplates(lib.templates);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load mock library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, lang, typ, domain, isGuest, authLoading]);

  // Personas are fetched once and reused by the detail modal so opening
  // a card is instant after the first call.
  useEffect(() => {
    if (authLoading || isGuest) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchPersonas(apiCall);
        if (!cancelled) setPersonas(r.personas);
      } catch {
        // non-fatal; detail modal will fall back to persona_id only
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, authLoading, isGuest]);

  const detailModalTemplates = useMemo(() => {
    const byId = new Map<string, InterviewTemplate>();
    for (const t of templates) {
      byId.set(t.id, loadedTemplateDetails[t.id] || libraryAsInterviewTemplate(t));
    }
    for (const t of Object.values(loadedTemplateDetails)) {
      byId.set(t.id, t);
    }
    return Array.from(byId.values());
  }, [templates, loadedTemplateDetails]);

  const loadTemplateDetail = async (templateId: string) => {
    if (loadedTemplateDetails[templateId]) return loadedTemplateDetails[templateId];
    const tpl = await getTemplate(apiCall, templateId);
    setLoadedTemplateDetails((prev) => ({ ...prev, [tpl.id]: tpl }));
    return tpl;
  };

  const blockIfNoPracticeQuota = (): boolean => {
    if (!quota) return true;
    if (quota.remaining > 0) return false;
    if (quota.total <= 0) setShowPaywall(true);
    else setRateLimitInfo(mockQuotaToRateLimitInfo(quota));
    return true;
  };

  const startPracticeCheckout = async () => {
    setCheckoutLoading(true);
    try {
      setCheckoutReturn('/?tab=practice');
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': uiLang || 'en',
        },
        body: JSON.stringify({
          plan_type: 'explorer_premium',
          billing_cycle: 'monthly',
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to start checkout');
      }
      const data = await response.json();
      if (!data.client_secret) throw new Error('Failed to start checkout');
      setShowPaywall(false);
      navigate('/checkout', { state: { clientSecret: data.client_secret, planType: 'explorer_premium' } });
    } catch (e: any) {
      setError(e?.message || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCustomizeFromTemplate = (
    tpl: InterviewTemplate,
    opts?: { sampleSteps?: InterviewQuestion[]; language?: 'de' | 'en' },
  ) => {
    if (!requireAuth()) return;
    if (blockIfNoPracticeQuota()) return;
    setDetailTemplate(null);
    const sorted = [...(tpl.questions || [])].sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
    );
    const steps =
      opts?.sampleSteps?.length
        ? opts.sampleSteps
        : tpl.sampling_strategy
          ? pickSampleTaskSteps(sorted, Math.floor(Math.random() * 1e9))
          : sorted;
    const dur = Number(tpl.duration_minutes) || 45;
    const snapped: 30 | 45 | 60 = dur <= 35 ? 30 : dur >= 55 ? 60 : 45;
    const tplType = (tpl.type || 'behavioral') as MockStartCustomPayload['type'];
    setCustomPrefill({
      topic: formatQuestionsAsCustomizeTopic(steps),
      type: tplType === 'scenario' ? 'behavioral' : tplType,
      difficulty: (tpl.difficulty as MockStartCustomPayload['difficulty']) || 'mid',
      duration_minutes: snapped,
      language: opts?.language ?? (tpl.language === 'en' ? 'en' : 'de'),
      persona_id: resolveVisiblePersonaId(tpl.persona_id, personas),
      enable_coding_panel: !!tpl.enable_coding_panel,
      enable_whiteboard_panel: !!tpl.enable_whiteboard_panel,
      enable_numbers_panel: !!tpl.enable_numbers_panel,
      enable_notepad_panel: !!tpl.enable_notepad_panel,
    });
    setShowCustomModal(true);
  };

  const openDetails = async (templateId: string) => {
    if (!requireAuth()) return;
    try {
      setDetailLoading(true);
      const tpl = await loadTemplateDetail(templateId);
      setDetailTemplate(tpl);
    } catch (e: any) {
      setError(e?.message || 'Failed to load template details');
    } finally {
      setDetailLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const byFam: Record<string, MockLibraryTemplate[]> = {};
    for (const t of templates) {
      const key = t.family_key || t.id;
      if (!byFam[key]) byFam[key] = [];
      byFam[key].push(t);
    }
    const groups = Object.values(byFam).map((variants) => {
      const en = variants.find((v) => v.language === 'en');
      const de = variants.find((v) => v.language === 'de');
      return { primary: en || de || variants[0], en, de };
    });
    // Client-side search: match across title + description in either
    // language so toggling language doesn't drop a card the user
    // already found.
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      const haystacks = [
        g.primary.title,
        g.primary.description,
        g.en?.title,
        g.en?.description,
        g.de?.title,
        g.de?.description,
      ];
      return haystacks.some((s) => (s || '').toLowerCase().includes(q));
    });
  }, [templates, search]);

  // When no specific Area-filter is active we group cards into domain
  // sections so the page doesn't look like Consulting / Finance / Tech
  // are randomly interleaved. "General" leads because behavioral
  // interviews apply to everyone regardless of role -- it's the most
  // universal entry point for someone new to practicing.
  const DOMAIN_ORDER: TemplateDomain[] = [
    'general', 'tech', 'product', 'sales_gtm', 'consulting',
    'marketing', 'finance',
  ];
  const sectionedGroups = useMemo(() => {
    if (domain !== 'all') {
      return [{ domain: null as TemplateDomain | null, items: grouped }];
    }
    const buckets: Record<string, typeof grouped> = {};
    for (const g of grouped) {
      const d = (g.primary.domain || 'general') as TemplateDomain;
      if (!buckets[d]) buckets[d] = [];
      buckets[d].push(g);
    }
    const sections: { domain: TemplateDomain | null; items: typeof grouped }[] = [];
    for (const d of DOMAIN_ORDER) {
      if (buckets[d]?.length) sections.push({ domain: d, items: buckets[d] });
    }
    // Any unmapped bucket (shouldn't happen post migration 024) lands
    // in a trailing "Other" section so nothing silently disappears.
    for (const d of Object.keys(buckets)) {
      if (!DOMAIN_ORDER.includes(d as TemplateDomain)) {
        sections.push({ domain: d as TemplateDomain, items: buckets[d] });
      }
    }
    return sections;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, domain]);

  // Card "Start" -> open consent gate. Actual session creation only
  // happens once the user explicitly ticks the consent checkbox and
  // confirms.
  const handleStart = (templateId: string, language: 'en' | 'de') => {
    if (!requireAuth()) return;
    if (blockIfNoPracticeQuota()) return;
    setError(null);
    setConsentChecked(false);
    setCustomContext('');
    const tpl = templates.find((t) => t.id === templateId);
    const isPool = !!tpl?.is_pool;
    const tplDur = Number(tpl?.duration_minutes) || 45;
    setTargetDifficulty('mid');
    const snapped = tplDur <= 37 ? 30 : tplDur <= 52 ? 45 : 60;
    setTargetDuration(snapped as 30 | 45 | 60);
    setConsentPersonaId(resolveVisiblePersonaId(tpl?.persona_id, personas));
    const familyKey = tpl?.family_key;
    const variants = familyKey
      ? templates.filter((t) => t.family_key === familyKey)
      : [];
    const enTpl = variants.find((t) => t.language === 'en');
    const deTpl = variants.find((t) => t.language === 'de');
    setConsentFor({
      templateId,
      language,
      isPool,
      templateDuration: tplDur,
      enTemplateId: enTpl?.id,
      deTemplateId: deTpl?.id,
    });
  };

  const handleConsentLanguageChange = (language: 'de' | 'en') => {
    setConsentFor((prev) => {
      if (!prev) return prev;
      const nextId =
        language === 'en'
          ? prev.enTemplateId || prev.templateId
          : prev.deTemplateId || prev.templateId;
      const nextTpl = templates.find((t) => t.id === nextId);
      return {
        ...prev,
        language,
        templateId: nextId,
        isPool: !!nextTpl?.is_pool,
        templateDuration: Number(nextTpl?.duration_minutes) || prev.templateDuration,
      };
    });
  };

  const handleCustomMockStart = async (payload: Omit<MockStartCustomPayload, 'explicit_consent'>) => {
    if (blockIfNoPracticeQuota()) return;
    try {
      setCustomStarting(true);
      setError(null);
      const res = await startCustomMock(apiCall, {
        ...payload,
        explicit_consent: true,
      });
      const token = res.invite_url.split('/').pop();
      setShowCustomModal(false);
      navigate(`/interview/invite/${token}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to start custom mock');
    } finally {
      setCustomStarting(false);
    }
  };

  const handleConsentConfirm = async () => {
    if (!consentFor || !consentChecked) return;
    const { templateId, language } = consentFor;
    try {
      setStarting(templateId);
      setError(null);
      const tpl = templates.find((t) => t.id === templateId);
      const ctx = customContext.trim();
      const res = await startMock(apiCall, {
        template_id: templateId,
        language,
        persona_id: resolveVisiblePersonaId(consentPersonaId || tpl?.persona_id, personas),
        // Treat the custom context as the role_description; that field
        // is already wired into the system prompt builder, so the AI
        // gets it as additional "ROLE / KONTEXT" guidance.
        role_description: ctx ? ctx.slice(0, 4000) : undefined,
        target_difficulty: targetDifficulty,
        target_duration_minutes: targetDuration,
        explicit_consent: true,
      });
      const token = res.invite_url.split('/').pop();
      navigate(`/interview/invite/${token}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to start mock');
      setStarting(null);
    } finally {
      setConsentFor(null);
    }
  };

  const T = uiLang === 'de' ? STRINGS_DE : STRINGS_EN;
  const canStartMock = isGuest || (quota?.remaining ?? 0) > 0;

  // Practice is explorer-only; recruiters should book a demo for hiring AI interviews.
  if (!authLoading && isSignedIn && userProfile?.is_recruiter) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <RecruiterExplorerGate />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 w-full flex-1 flex flex-col">

        {!embedded && (
        <header className="flex items-center justify-between gap-3 mb-6 sm:mb-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-500" />
            <span className="text-sm font-semibold tracking-wide text-slate-900">Cevee Simulations</span>
            <span className="ml-1 text-[10px] uppercase tracking-wider font-semibold text-pink-700 bg-pink-50 border border-pink-200 px-1.5 py-0.5 rounded-full">
              Beta
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="text-xs font-medium text-slate-500 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100 transition"
            >
              {uiLang.toUpperCase()}
            </button>
              <button
                type="button"
                onClick={() => requireAuth(() => navigate('/mock/history'))}
              className="text-xs font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-slate-100 transition"
            >
              <HistoryIcon className="w-3.5 h-3.5" />
              {T.history}
              </button>
              <button
                type="button"
                onClick={() => setShowPracticeInfo(true)}
                aria-label={T.libraryInfoShort}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-pink-600 px-2 py-1 rounded-full hover:bg-pink-50 transition-colors"
              >
                <Info className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{T.libraryInfoShort}</span>
              </button>
          </div>
        </header>
        )}

        <section className={embedded ? "mb-5 sm:mb-6" : "mb-8 sm:mb-10"}>
          {/* Compact hero in embedded mode -- the Practice tab title in
              the parent tab bar already conveys what this page is, so
              we collapse the sub-eyebrow + verbose body paragraph into
              one short line and a value-pill row. The standalone /mock
              landing keeps the full hero (used by deep links from old
              marketing). */}
          {embedded ? (
            <>
              {/* Centered hero matching Inspire's layout. The Beta marker
                  lives on the tab itself (Home2) so we drop it here. The
                  highlight word ("privat" / "privately") gets the same
                  rose-gradient treatment used elsewhere on the page so
                  the Practice tab reads as part of the consumer brand
                  (no purple/fuchsia drift). */}
              <div className="text-center">
                <h1 className="text-xl sm:text-[1.65rem] font-bold text-gray-900 leading-tight">
                  {(() => {
                    const raw = T.heroTitleEmbedded;
                    const m = raw.match(/^(.*)\{(.+?)\}(.*)$/);
                    if (!m) return raw;
                    const [, before, hl, after] = m;
                    return (
                      <>
                        {before}
                        {hl}
                        {after}
                      </>
                    );
                  })()}
                </h1>
                <div className="mt-3 flex flex-wrap gap-1.5 justify-center">
                  <ValueChip icon={<ClipboardList className="w-3 h-3" />} label={T.pillScorecard} />
                  <ValueChip icon={<Video className="w-3 h-3" />} label={T.pillReplay} />
                  <ValueChip icon={<Lock className="w-3 h-3" />} label={T.pillPrivate} />
                </div>
                <div className="mt-3 flex items-center justify-center gap-4 sm:gap-6">
                  <button
                    type="button"
                    onClick={() => requireAuth(() => navigate('/mock/history'))}
                    className="text-xs font-medium text-slate-500 hover:text-slate-900 flex items-center gap-1.5 px-2.5 py-1 rounded-full hover:bg-slate-100 transition"
                  >
                    <HistoryIcon className="w-3.5 h-3.5" />
                    {T.history}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPracticeInfo(true)}
                    aria-label={T.libraryInfoShort}
                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-pink-600 px-2.5 py-1 rounded-full hover:bg-pink-50 transition-colors"
                  >
                    <Info className="w-3.5 h-3.5" />
                    {T.libraryInfoShort}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-pink-600 mb-1.5">
            {T.heroSub}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            {T.heroTitle}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-2xl leading-relaxed">
            {T.heroBody}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <ValueChip icon={<ClipboardList className="w-3.5 h-3.5" />} label={T.pillScorecard} />
            <ValueChip icon={<Video className="w-3.5 h-3.5" />} label={T.pillReplay} />
            <ValueChip icon={<Lock className="w-3.5 h-3.5" />} label={T.pillPrivate} />
          </div>
            </>
          )}
        </section>

        {error && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-2 text-sm text-rose-900">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <CustomMockCallout
          uiLang={uiLang}
          T={T}
          canStart={canStartMock}
          onClick={() => requireAuth(() => {
            if (!blockIfNoPracticeQuota()) setShowCustomModal(true);
          })}
        />

        {/* Cevee template library — collapsed by default so the page leads
            with "build your own"; expand to browse the ready-made formats. */}
        <section className="mt-6 sm:mt-8">
          <button
            type="button"
            onClick={() => setShowLibrary((v) => !v)}
            aria-expanded={showLibrary}
            className="w-full flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-pink-50 border border-pink-100 text-pink-600 flex-shrink-0">
                <LayoutGrid className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 leading-tight">
                  {T.libraryToggleTitle}
                </p>
                <p className="text-[11px] sm:text-xs text-slate-500 leading-snug truncate">
                  {T.libraryToggleSubtitle}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {grouped.length > 0 && (
                <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                  {grouped.length}
                </span>
              )}
              <ChevronDown
                className={'w-4 h-4 text-slate-400 transition-transform ' + (showLibrary ? 'rotate-180' : '')}
              />
            </div>
          </button>
        </section>

        {showLibrary && (
        <>
        <section className="mt-4 sm:mt-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Sliders className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-700">{T.filters}</h2>
              {!embedded && (
                <button
                  type="button"
                  onClick={() => setShowPracticeInfo(true)}
                  aria-label={T.libraryInfoShort}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-slate-400 hover:text-pink-600 hover:bg-pink-50 transition-colors"
                >
                  <Info className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-medium hidden sm:inline">{T.libraryInfoShort}</span>
                </button>
              )}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={T.searchPlaceholder}
                className="w-full pl-8 pr-7 py-1.5 text-sm rounded-full border border-slate-200 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-300"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2.5">
            <FilterGroup
              label={T.area}
              value={domain}
              onChange={(v) => setDomain(v as DomainFilter)}
              options={[
                { v: 'all', label: T.all },
                { v: 'general', label: T.areaGeneral },
                { v: 'tech', label: T.areaTech },
                { v: 'product', label: T.areaProduct },
                { v: 'sales_gtm', label: T.areaSales },
                { v: 'consulting', label: T.areaConsulting },
                { v: 'marketing', label: T.areaMarketing },
                { v: 'finance', label: T.areaFinance },
              ]}
            />
            <div className="flex flex-col sm:flex-row gap-2.5">
              <FilterGroup
                label={T.language}
                value={lang}
                onChange={(v) => setLang(v as LangFilter)}
                options={[
                  { v: 'all', label: T.all },
                  { v: 'de', label: 'DE' },
                  { v: 'en', label: 'EN' },
                ]}
              />
              <FilterGroup
                label={T.type}
                value={typ}
                onChange={(v) => setTyp(v as TypeFilter)}
                options={[
                  { v: 'all', label: T.all },
                  { v: 'behavioral', label: T.behav },
                  { v: 'coding', label: T.coding },
                  { v: 'system_design', label: T.systemDesign },
                  { v: 'scenario', label: T.scenario },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="mt-6">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : grouped.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">{T.empty}</div>
          ) : (
            <div className="space-y-7">
              {sectionedGroups.map((section) => (
                <div key={section.domain || 'all'}>
                  {section.domain && (
                    <div className="mb-3 flex items-baseline gap-2">
                      <h2 className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide sm:tracking-wider text-slate-500">
                        {domainLabel(section.domain, T)}
                      </h2>
                      <span className="text-[10px] font-medium text-slate-400">
                        {section.items.length} {section.items.length === 1 ? T.templateSingular : T.templatePlural}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {section.items.map((g) => (
                      <TemplateCard
                        key={g.primary.family_key || g.primary.id}
                        template={g.primary}
                        en={g.en}
                        de={g.de}
                        uiLang={uiLang}
                        starting={starting}
                        canStart={canStartMock}
                        onStart={handleStart}
                        onOpenDetails={openDetails}
                        T={T}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        </>
        )}

        <PrivacyFooter uiLang={uiLang} />
      </div>

      <TemplateDetailModal
        isOpen={!!detailTemplate}
        templates={detailModalTemplates}
        initial={detailTemplate}
        personas={personas}
        onClose={() => setDetailTemplate(null)}
        onUseAsTemplate={handleCustomizeFromTemplate}
        onNeedTemplate={(id) => { void loadTemplateDetail(id); }}
        canUseAsTemplate
        sessionConfigurable
        onConfigureSimulation={(tpl) => {
          setDetailTemplate(null);
          const langCode = tpl.language === 'de' ? 'de' : 'en';
          handleStart(tpl.id, langCode);
        }}
      />

      <ConsentModal
        isOpen={!!consentFor}
        uiLang={uiLang}
        checked={consentChecked}
        busy={!!starting}
        personas={personas}
        personaId={consentPersonaId}
        onPersonaChange={setConsentPersonaId}
        customContext={customContext}
        onCustomContextChange={setCustomContext}
        onCheck={setConsentChecked}
        sessionLanguage={consentFor?.language ?? 'de'}
        bilingual={!!(consentFor?.enTemplateId && consentFor?.deTemplateId)}
        onSessionLanguageChange={handleConsentLanguageChange}
        difficulty={targetDifficulty}
        onDifficultyChange={setTargetDifficulty}
        duration={targetDuration}
        onDurationChange={setTargetDuration}
        onCancel={() => setConsentFor(null)}
        onConfirm={handleConsentConfirm}
      />

      <CustomMockModal
        isOpen={showCustomModal}
        uiLang={uiLang}
        T={T}
        canStart={canStartMock}
        starting={customStarting}
        personas={personas}
        prefill={customPrefill}
        onClose={() => {
          setShowCustomModal(false);
          setCustomPrefill(null);
        }}
        onSubmit={handleCustomMockStart}
      />

      {detailLoading && (
        <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}

      {rateLimitInfo && (
        <RateLimitNotification
          rateLimitInfo={rateLimitInfo}
          feature="mock_simulations"
          onClose={() => setRateLimitInfo(null)}
        />
      )}

      {showPaywall && (
        <PracticePaywallModal
          T={T}
          loading={checkoutLoading}
          onClose={() => setShowPaywall(false)}
          onSubscribe={startPracticeCheckout}
          onSeePricing={() => {
            setCheckoutReturn('/?tab=practice');
            navigate('/pricing');
          }}
        />
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {showPracticeInfo && (
        <PracticeInfoModal T={T} onClose={() => setShowPracticeInfo(false)} />
      )}
    </div>
  );
};


const PracticePaywallModal: React.FC<{
  T: typeof STRINGS_EN;
  loading: boolean;
  onClose: () => void;
  onSubscribe: () => void;
  onSeePricing: () => void;
}> = ({ T, loading, onClose, onSubscribe, onSeePricing }) => (
  <div
    className="fixed inset-0 z-[10020] flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-4"
    onClick={onClose}
    role="dialog"
    aria-modal="true"
    aria-labelledby="practice-paywall-title"
  >
    <div
      className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-200 p-5 sm:p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-pink-50 border border-pink-100 flex items-center justify-center flex-shrink-0">
            <Crown className="w-4 h-4 text-pink-600" />
          </div>
          <h3 id="practice-paywall-title" className="text-base font-semibold text-slate-900 leading-snug">
            {T.paywallTitle}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{T.paywallBody}</p>
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{T.paywallQuota}</span>
          <span className="font-semibold text-slate-900">{T.paywallPrice}</span>
        </div>
        <p className="text-[12px] text-slate-500">{T.paywallPrescreen}</p>
      </div>
      <button
        type="button"
        onClick={onSubscribe}
        disabled={loading}
        className="mt-5 w-full py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-semibold hover:from-pink-600 hover:to-rose-600 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
        {T.paywallSubscribe}
      </button>
      <button
        type="button"
        onClick={onSeePricing}
        className="mt-2 w-full py-2 text-sm font-medium text-slate-500 hover:text-slate-800"
      >
        {T.paywallSeePricing}
      </button>
    </div>
  </div>
);

const PracticeInfoModal: React.FC<{
  T: typeof STRINGS_EN;
  onClose: () => void;
}> = ({ T, onClose }) => (
  <div
    className="fixed inset-0 z-[10020] flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-4"
    onClick={onClose}
    role="dialog"
    aria-modal="true"
    aria-labelledby="practice-info-title"
  >
    <div
      className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-200 p-5 sm:p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-pink-50 border border-pink-100 flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4 text-pink-600" />
          </div>
          <h3 id="practice-info-title" className="text-base font-semibold text-slate-900 leading-snug">
            {T.libraryInfoTitle}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex-shrink-0"
          aria-label={T.libraryInfoClose}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mb-4">
        {T.libraryInfoLead}
      </p>
      <div className="mb-4">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
          {T.libraryInfoFeaturesHeading}
        </h4>
        <ul className="space-y-2 text-sm text-slate-600 leading-relaxed">
          <li className="flex gap-2">
            <span className="text-pink-500 mt-0.5">•</span>
            <span>{T.libraryInfoFeature1}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-pink-500 mt-0.5">•</span>
            <span>{T.libraryInfoFeature2}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-pink-500 mt-0.5">•</span>
            <span>{T.libraryInfoFeature3}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-pink-500 mt-0.5">•</span>
            <span>{T.libraryInfoFeature4}</span>
          </li>
        </ul>
      </div>
      <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
          {T.libraryInfoHowHeading}
        </h4>
        <ul className="space-y-2 text-sm text-slate-600 leading-relaxed">
          <li className="flex gap-2">
            <span className="text-pink-500 font-semibold mt-0.5">1.</span>
            <span>{T.libraryInfoBullet1}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-pink-500 font-semibold mt-0.5">2.</span>
            <span>{T.libraryInfoBullet2}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-pink-500 font-semibold mt-0.5">3.</span>
            <span>{T.libraryInfoBullet3}</span>
          </li>
        </ul>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="mt-5 w-full py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-pink-600 transition-colors"
      >
        {T.libraryInfoClose}
      </button>
    </div>
  </div>
);


const CustomMockCallout: React.FC<{
  uiLang: 'de' | 'en';
  T: typeof STRINGS_EN;
  canStart: boolean;
  onClick: () => void;
}> = ({ uiLang, T, canStart, onClick }) => (
  <section className="mt-6 sm:mt-8">
    <button
      type="button"
      onClick={onClick}
      title={!canStart ? (uiLang === 'de' ? 'Quota erschöpft - Klick für Upgrade' : 'Quota exhausted - click to upgrade') : ''}
      className="w-full flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-pink-50 border border-pink-100 text-pink-600 flex-shrink-0">
          <Wand2 className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 leading-tight">
            {T.customTitle}
          </p>
          <p className="text-[11px] sm:text-xs text-slate-500 leading-snug truncate">
            {T.customBody}
          </p>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
    </button>
  </section>
);


const CustomMockModal: React.FC<{
  isOpen: boolean;
  uiLang: 'de' | 'en';
  T: typeof STRINGS_EN;
  canStart: boolean;
  starting: boolean;
  personas: Persona[];
  prefill: {
    topic: string;
    type: MockStartCustomPayload['type'];
    difficulty: MockStartCustomPayload['difficulty'];
    duration_minutes: MockStartCustomPayload['duration_minutes'];
    language: 'de' | 'en';
    persona_id: string;
    enable_coding_panel: boolean;
    enable_whiteboard_panel: boolean;
    enable_numbers_panel: boolean;
    enable_notepad_panel: boolean;
  } | null;
  onClose: () => void;
  onSubmit: (payload: Omit<MockStartCustomPayload, 'explicit_consent'>) => void;
}> = ({ isOpen, uiLang, T, canStart, starting, personas, prefill, onClose, onSubmit }) => {
  const [topic, setTopic] = useState('');
  const [type, setType] = useState<MockStartCustomPayload['type']>('behavioral');
  const [difficulty, setDifficulty] = useState<MockStartCustomPayload['difficulty']>('mid');
  const [duration, setDuration] = useState<MockStartCustomPayload['duration_minutes']>(30);
  const [language, setLanguage] = useState<'de' | 'en'>(uiLang);
  const [personaId, setPersonaId] = useState<string>(DEFAULT_PERSONA_ID);
  const [enableCoding, setEnableCoding] = useState(false);
  const [enableWhiteboard, setEnableWhiteboard] = useState(false);
  const [enableNumbers, setEnableNumbers] = useState(false);
  const [enableNotepad, setEnableNotepad] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Default side panel(s) for the chosen interview type.
    if (type === 'coding') setEnableCoding(true);
    if (type === 'system_design') setEnableWhiteboard(true);
    // Behavioral = voice-only; notepad stays off. Scenario/consulting may use it.
    if (type === 'scenario') setEnableNotepad(true);
  }, [type, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setTopic('');
      setType('behavioral');
      setDifficulty('mid');
      setDuration(30);
      setLanguage(uiLang);
      setPersonaId(DEFAULT_PERSONA_ID);
      setEnableCoding(false);
      setEnableWhiteboard(false);
      setEnableNumbers(false);
      setEnableNotepad(false);
      setConsent(false);
      return;
    }
    if (prefill) {
      setTopic(prefill.topic);
      setType(prefill.type);
      setDifficulty(prefill.difficulty);
      setDuration(prefill.duration_minutes);
      setLanguage(prefill.language);
      setPersonaId(resolveVisiblePersonaId(prefill.persona_id, personas));
      setEnableCoding(prefill.enable_coding_panel);
      setEnableWhiteboard(prefill.enable_whiteboard_panel);
      setEnableNumbers(prefill.enable_numbers_panel);
      setEnableNotepad(prefill.enable_notepad_panel);
    }
  }, [isOpen, uiLang, prefill, personas]);

  if (!isOpen) return null;
  // Submit stays clickable even when out of quota -- the parent handler
  // shows the rate-limit notification + upgrade CTA. Locking the button
  // grey forces a confused user to read a tooltip; making it clickable
  // funnels them straight into pricing.
  const canSubmit = consent && topic.trim().length >= 20 && !starting;
  const remaining = 4000 - topic.length;

  return (
    <div className="fixed inset-0 z-[10010] flex items-start sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-mock-title"
        className="w-full max-w-lg rounded-3xl bg-white shadow-2xl my-4"
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-2xl bg-pink-50 border border-pink-100 text-pink-600 flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h2 id="custom-mock-title" className="text-base font-semibold text-slate-900">
                  {T.customModalTitle}
                </h2>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">{T.customModalLead}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 p-1"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="custom-topic" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                {T.customTopicLabel} <span className="text-pink-500 normal-case font-normal">*</span>
              </label>
              <textarea
                id="custom-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value.slice(0, 4000))}
                rows={3}
                placeholder={T.customTopicPlaceholder}
                disabled={starting}
                className="w-full text-sm rounded-2xl border border-slate-200 bg-white px-3 py-2.5 leading-relaxed text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300 resize-y disabled:opacity-60"
              />
              <p className="mt-1 text-[11px] text-slate-400 leading-relaxed">
                {T.customTopicHint}
                {topic.length > 0 && (
                  <span className="ml-1 text-slate-300">· {remaining} {T.charsLeft}</span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <CustomField label={T.customType}>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as MockStartCustomPayload['type'])}
                  className="w-full text-sm rounded-xl border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
                >
                  <option value="behavioral">{T.typeBehav}</option>
                  <option value="coding">{T.typeCoding}</option>
                  <option value="system_design">{T.typeSystemDesign}</option>
                  <option value="scenario">{T.typeScenario}</option>
                </select>
              </CustomField>
              <CustomField label={T.customLevel}>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as MockStartCustomPayload['difficulty'])}
                  className="w-full text-sm rounded-xl border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
                >
                  <option value="student">{T.lvlStudent}</option>
                  <option value="junior">Junior</option>
                  <option value="mid">Mid</option>
                  <option value="senior">Senior</option>
                </select>
              </CustomField>
              <CustomField label={T.customDuration}>
                <select
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value, 10) as MockStartCustomPayload['duration_minutes'])}
                  className="w-full text-sm rounded-xl border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                </select>
              </CustomField>
              <CustomField label={T.customLanguage}>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as 'de' | 'en')}
                  className="w-full text-sm rounded-xl border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300"
                >
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </CustomField>
            </div>

            {personas.length > 0 && (
              <div>
                <p className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {T.customPersona}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {personas.map((p) => {
                    const active = personaId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPersonaId(p.id)}
                        className={
                          'group rounded-2xl border px-3 py-2.5 text-left transition-colors ' +
                          (active
                            ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                            : 'border-slate-200 bg-white hover:border-slate-300')
                        }
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${p.gradient || 'from-slate-400 to-slate-600'} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm`}>
                            {p.display_name[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className={'text-xs font-semibold truncate ' + (active ? 'text-white' : 'text-slate-900')}>
                              {p.display_name}
                            </div>
                            <div className={'text-[10px] truncate ' + (active ? 'text-white/70' : 'text-slate-500')}>
                              {uiLang === 'de' ? p.vibe_de : p.vibe_en}
                            </div>
                          </div>
                          <PersonaVoicePlayButton
                            personaId={p.id}
                            language={language}
                            displayName={p.display_name}
                            variant={active ? 'dark' : 'light'}
                            voiceSpeed={p.voice_speed}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <p className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                {T.customPanels}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setEnableCoding((v) => !v)}
                  className={
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ' +
                    (enableCoding
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                  }
                >
                  <CodeIcon className="w-3.5 h-3.5" />
                  {T.customPanelCoding}
                </button>
                <button
                  type="button"
                  onClick={() => setEnableWhiteboard((v) => !v)}
                  className={
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ' +
                    (enableWhiteboard
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                  }
                >
                  <PenTool className="w-3.5 h-3.5" />
                  {T.customPanelWB}
                </button>
                <button
                  type="button"
                  onClick={() => setEnableNumbers((v) => !v)}
                  className={
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ' +
                    (enableNumbers
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')
                  }
                >
                  <Calculator className="w-3.5 h-3.5" />
                  {T.customPanelNumbers}
                </button>
                <button
                  type="button"
                  onClick={() => setEnableNotepad((v) => !v)}
                  className={
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ' +
                    (enableNotepad
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-amber-50 hover:border-amber-200')
                  }
                  title={T.customPanelNotepadHint}
                >
                  <FileTextIcon className="w-3.5 h-3.5" />
                  {T.customPanelNotepad}
                </button>
              </div>
              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                {T.screenShareNote}
              </p>
            </div>
          </div>

          <label className="mt-5 flex items-start gap-2.5 p-3 rounded-2xl border border-slate-200 cursor-pointer hover:bg-slate-50/60">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="text-sm text-slate-800 font-medium">{T.consentCheckbox}</span>
          </label>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              disabled={starting}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 px-4 py-2 rounded-full hover:bg-slate-100 transition disabled:opacity-50"
            >
              {T.consentCancel}
            </button>
            <button
              onClick={() => {
                if (!canSubmit) return;
                onSubmit({
                  topic: topic.trim(),
                  type,
                  difficulty,
                  duration_minutes: duration,
                  language,
                  persona_id: personaId,
                  enable_coding_panel: enableCoding,
                  enable_whiteboard_panel: enableWhiteboard,
                  enable_numbers_panel: enableNumbers,
                  enable_notepad_panel: enableNotepad,
                });
              }}
              disabled={!canSubmit}
              className={
                'inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition ' +
                (canSubmit
                  ? 'bg-slate-900 text-white hover:bg-pink-600'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed')
              }
            >
              {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              {starting ? T.starting : T.customStart}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const CustomField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
      {label}
    </label>
    {children}
  </div>
);


/** Compact persona row for mock start modals */
const PersonaPickerCompact: React.FC<{
  personas: Persona[];
  personaId: string;
  onChange: (id: string) => void;
  interviewLang: 'de' | 'en';
  uiLang: 'de' | 'en';
  label: string;
  disabled?: boolean;
}> = ({ personas, personaId, onChange, interviewLang, uiLang, label, disabled }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
      {personas.map((p) => {
        const active = personaId === p.id;
        return (
          <button
            key={p.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(p.id)}
            className={
              'rounded-xl border px-2.5 py-2 text-left transition-colors ' +
              (active
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white hover:border-slate-300')
            }
          >
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full bg-gradient-to-br ${p.gradient || 'from-slate-400 to-slate-600'} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}
              >
                {p.display_name[0]}
              </div>
              <div className="min-w-0 flex-1">
                <div className={'text-[11px] font-semibold truncate ' + (active ? 'text-white' : 'text-slate-900')}>
                  {p.display_name}
                </div>
                <div className={'text-[10px] truncate ' + (active ? 'text-white/70' : 'text-slate-500')}>
                  {uiLang === 'de' ? p.vibe_de : p.vibe_en}
                </div>
              </div>
              <span onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                <PersonaVoicePlayButton
                  personaId={p.id}
                  language={interviewLang}
                  displayName={p.display_name}
                  variant={active ? 'dark' : 'light'}
                  voiceSpeed={p.voice_speed}
                />
              </span>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const ConsentModal: React.FC<{
  isOpen: boolean;
  uiLang: 'de' | 'en';
  checked: boolean;
  busy: boolean;
  personas: Persona[];
  personaId: string;
  onPersonaChange: (id: string) => void;
  customContext: string;
  onCustomContextChange: (v: string) => void;
  onCheck: (v: boolean) => void;
  sessionLanguage: 'de' | 'en';
  bilingual?: boolean;
  onSessionLanguageChange: (v: 'de' | 'en') => void;
  difficulty: 'student' | 'junior' | 'mid' | 'senior';
  onDifficultyChange: (v: 'student' | 'junior' | 'mid' | 'senior') => void;
  duration: 30 | 45 | 60;
  onDurationChange: (v: 30 | 45 | 60) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({
  isOpen, uiLang, checked, busy, personas, personaId, onPersonaChange,
  customContext, onCustomContextChange, onCheck,
  sessionLanguage, bilingual, onSessionLanguageChange,
  difficulty, onDifficultyChange, duration, onDurationChange, onCancel, onConfirm,
}) => {
  const [showConsentDetails, setShowConsentDetails] = useState(false);
  if (!isOpen) return null;
  const T = uiLang === 'de' ? STRINGS_DE : STRINGS_EN;
  const remaining = 4000 - customContext.length;

  const chip = (active: boolean) =>
    active
      ? 'bg-pink-50 border-pink-300 text-pink-800'
      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50';

  return (
    <div className="fixed inset-0 z-[10010] flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-3 sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mock-consent-title"
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl flex flex-col max-h-[min(90vh,640px)] my-2"
      >
        <div className="flex-shrink-0 px-4 pt-4 pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-pink-50 border border-pink-100 text-pink-600 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 id="mock-consent-title" className="text-sm font-semibold text-slate-900">
                {T.consentTitle}
              </h2>
              <p className="text-[11px] text-slate-500 leading-snug">{T.consentLead}</p>
            </div>
            </div>
          </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
          {bilingual && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {uiLang === 'de' ? 'Sprache' : 'Language'}
              </span>
              <div className="inline-flex bg-slate-100 rounded-full p-0.5 text-[11px] font-semibold">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSessionLanguageChange('en')}
                  className={'px-2.5 py-0.5 rounded-full transition ' + (sessionLanguage === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}
                >EN</button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSessionLanguageChange('de')}
                  className={'px-2.5 py-0.5 rounded-full transition ' + (sessionLanguage === 'de' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}
                >DE</button>
              </div>
            </div>
          )}

          {personas.length > 0 && (
            <PersonaPickerCompact
              personas={personas}
              personaId={personaId}
              onChange={onPersonaChange}
              interviewLang={sessionLanguage}
              uiLang={uiLang}
              label={T.consentPersona}
              disabled={busy}
            />
          )}

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              {uiLang === 'de' ? 'Level & Dauer' : 'Level & duration'}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(['student', 'junior', 'mid', 'senior'] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      disabled={busy}
                  onClick={() => onDifficultyChange(d)}
                  className={'px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ' + chip(difficulty === d)}
                >
                  {d === 'student' ? (uiLang === 'de' ? 'Student' : 'Student') : d.charAt(0).toUpperCase() + d.slice(1)}
                </button>
              ))}
                      </div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {([30, 45, 60] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  disabled={busy}
                  onClick={() => onDurationChange(d)}
                  className={'px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ' + chip(duration === d)}
                >
                  {d} {uiLang === 'de' ? 'Min' : 'min'}
                    </button>
              ))}
              </div>
            </div>

          <div>
            <label htmlFor="mock-context" className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {T.contextLabel}
            </label>
            <textarea
              id="mock-context"
              value={customContext}
              onChange={(e) => onCustomContextChange(e.target.value.slice(0, 4000))}
              rows={2}
              placeholder={T.contextPlaceholder}
              disabled={busy}
              className="mt-1 w-full text-sm rounded-xl border border-slate-200 bg-white px-2.5 py-2 leading-snug text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-200 focus:border-pink-300 resize-none disabled:opacity-60"
            />
                {customContext.length > 0 && (
              <p className="mt-0.5 text-[10px] text-slate-400 text-right">{remaining} {T.charsLeft}</p>
            )}
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
            <p className="text-[11px] text-slate-600 leading-snug">{T.consentSummary}</p>
            <button
              type="button"
              onClick={() => setShowConsentDetails((v) => !v)}
              className="mt-1 text-[10px] font-semibold text-slate-500 hover:text-slate-800"
            >
              {showConsentDetails ? T.consentDetailsHide : T.consentDetailsShow}
            </button>
            {showConsentDetails && (
              <ul className="mt-1.5 space-y-1 text-[10px] text-slate-600 leading-snug list-disc pl-4">
                <li>{T.consentBullet1}</li>
                <li>{T.consentBullet2}</li>
                <li>{T.consentBullet3}</li>
                <li>{T.consentBullet4}</li>
          </ul>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 px-4 py-3 border-t border-slate-100 bg-white rounded-b-2xl space-y-2">
          <label className="flex items-start gap-2 p-2 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50/80">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => onCheck(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="text-[12px] text-slate-800 leading-snug">
              <span className="font-medium">{T.consentCheckbox}</span>
              <span className="text-slate-400">
                {' '}
                <Link to="/privacy" className="underline hover:text-slate-600">{T.privacyLink}</Link>
            {' · '}
                <Link to="/terms" className="underline hover:text-slate-600">{T.termsLink}</Link>
              </span>
            </span>
          </label>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100 transition disabled:opacity-50"
            >
              {T.consentCancel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!checked || busy}
              className={
                'inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-full transition ' +
                (checked && !busy
                  ? 'bg-slate-900 text-white hover:bg-pink-600'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed')
              }
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              {busy ? T.starting : T.consentConfirm}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const ValueChip: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 px-2.5 py-1 rounded-full shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
    <span className="text-pink-600">{icon}</span>
    {label}
  </span>
);


const PrivacyFooter: React.FC<{ uiLang: 'de' | 'en' }> = ({ uiLang }) => {
  const T = uiLang === 'de' ? STRINGS_DE : STRINGS_EN;
  return (
    <div className="mt-auto pt-8 sm:pt-10 pb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
      <span className="inline-flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" />
        {T.privacyTitle}
      </span>
      <Link to="/privacy" className="hover:text-slate-700 underline-offset-2 hover:underline">
        {T.privacyLink}
      </Link>
      <Link to="/terms" className="hover:text-slate-700 underline-offset-2 hover:underline">
        {T.termsLink}
      </Link>
      <Link to="/legal/avv" className="hover:text-slate-700 underline-offset-2 hover:underline">
        {T.avvLink}
      </Link>
    </div>
  );
};


const FilterGroup: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { v: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div className="flex-1 min-w-0">
    <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">{label}</label>
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => (
        <button
          key={opt.v}
          onClick={() => onChange(opt.v)}
          className={
            'text-[11px] font-medium px-2.5 py-1 rounded-full border transition ' +
            (value === opt.v
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400')
          }
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);


const TemplateCard: React.FC<{
  template: MockLibraryTemplate;
  en?: MockLibraryTemplate;
  de?: MockLibraryTemplate;
  uiLang: 'de' | 'en';
  starting: string | null;
  canStart: boolean;
  onStart: (id: string, lang: 'en' | 'de') => void;
  onOpenDetails: (id: string) => void;
  T: typeof STRINGS_EN;
}> = ({ template, en, de, uiLang, starting, canStart, onStart, onOpenDetails, T }) => {
  const [pickedLang, setPickedLang] = useState<'en' | 'de'>(() => {
    if (uiLang === 'de' && de) return 'de';
    if (uiLang === 'en' && en) return 'en';
    return (template.language as 'en' | 'de') || 'en';
  });
  // Follow the global header language toggle: when the user flips
  // EN <-> DE in the Cevee header, switch every card's default
  // variant accordingly (only when the requested variant exists).
  useEffect(() => {
    if (uiLang === 'de' && de && pickedLang !== 'de') setPickedLang('de');
    else if (uiLang === 'en' && en && pickedLang !== 'en') setPickedLang('en');
  }, [uiLang, en, de]);  // eslint-disable-line react-hooks/exhaustive-deps
  const active = (pickedLang === 'en' ? en : de) || template;
  const busy = starting === active.id;
  const panelTags: string[] = [];
  if (active.enable_coding_panel) panelTags.push(T.panelCode);
  if (active.enable_whiteboard_panel) panelTags.push(T.panelWb);
  if (active.enable_numbers_panel) panelTags.push(T.panelNumbers);
  if (active.enable_notepad_panel) panelTags.push(T.panelNotepad);
  return (
    <div className="group rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
            {active.domain && (
              <span className="inline-flex items-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-pink-700 bg-pink-50 border border-pink-100 px-1.5 py-0.5 rounded-full">
                {domainLabel(active.domain as TemplateDomain, T)}
              </span>
            )}
            {(active.pool_side === 'practice' || active.is_pool) && (
              <span
                className="inline-flex items-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full"
                title={uiLang === 'de'
                  ? 'Übungspool, getrennt vom Hiring-Pool bei echten Bewerbungen'
                  : 'Practice pool, separate from the hiring pool used in real interviews'}
              >
                {uiLang === 'de' ? 'Übung' : 'Practice'}
              </span>
            )}
            {active.type && (
              <span className="inline-flex items-center text-[9px] sm:text-[10px] font-medium text-slate-500">
                {labelForType(active.type, T)}
              </span>
            )}
          </div>
          <h3 className="mt-1 text-[15px] sm:text-base font-semibold text-slate-900 leading-snug">
            {cleanTitle(active.title)}
          </h3>
          {active.description && (
            <p className="mt-1.5 text-[13px] sm:text-sm text-slate-600 line-clamp-3">
              {sanitizeInterviewCopy(active.description)}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {T.durationAtStart}
            </span>
            {active.is_pool && active.steps_per_session != null && active.steps_per_session > 0 && (() => {
              const cases = Number(active.sampling_strategy?.total) || 1;
              const multi = cases > 1;
              const label = uiLang === 'de'
                ? (multi
                    ? `${cases} Cases · je ~${active.steps_per_session} Schritte`
                    : `~${active.steps_per_session} Schritte`)
                : (multi
                    ? `${cases} cases · ~${active.steps_per_session} steps each`
                    : `~${active.steps_per_session} steps`);
              const tip = uiLang === 'de'
                ? (multi
                    ? `${cases} Cases pro Session, je mehrere Schritte aus dem Pool`
                    : 'Pro Session ein Case mit mehreren Schritten aus dem Pool')
                : (multi
                    ? `${cases} cases per session, several steps each from the pool`
                    : 'One case per session with several steps from the pool');
              return (
                <span
                  className="inline-flex items-center gap-1 text-pink-700 font-semibold"
                  title={tip}
                >
                  <Shuffle className="w-3 h-3" />
                  {label}
                </span>
              );
            })()}
            {panelTags.length > 0 && panelTags.map((p) => (
              <span key={p} className="inline-flex items-center gap-1 text-pink-600 font-medium">
                <CheckCircle2 className="w-3 h-3" />{p}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {en && de ? (
            <div className="inline-flex bg-slate-100 rounded-full p-0.5 text-[11px] font-semibold">
              <button
                onClick={() => setPickedLang('en')}
                className={'px-2.5 py-0.5 rounded-full transition ' + (pickedLang === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}
              >EN</button>
              <button
                onClick={() => setPickedLang('de')}
                className={'px-2.5 py-0.5 rounded-full transition ' + (pickedLang === 'de' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}
              >DE</button>
            </div>
          ) : (
            <span className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
              {active.language}
            </span>
          )}
          <button
            onClick={() => onOpenDetails(active.id)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100 transition"
            title={T.viewDetails}
          >
            <Eye className="w-3 h-3" />
            {T.viewDetails}
          </button>
        </div>
        <button
          onClick={() => onStart(active.id, pickedLang)}
          disabled={busy}
          className={
            'inline-flex items-center gap-1 sm:gap-1.5 text-[13px] sm:text-sm font-semibold px-2.5 sm:px-3 py-1.5 rounded-full transition ' +
            (busy
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : canStart
              ? 'bg-slate-900 text-white hover:bg-pink-600'
                : 'bg-slate-900 text-white hover:bg-pink-600 ring-1 ring-pink-200')
          }
          title={!canStart ? (uiLang === 'de' ? 'Quota erschöpft - Klick für Upgrade' : 'Quota exhausted - click to upgrade') : ''}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
          {busy ? T.starting : T.startMock}
        </button>
      </div>
    </div>
  );
};


const cleanTitle = cleanTemplateTitle;

function domainLabel(d: TemplateDomain, T: typeof STRINGS_EN): string {
  switch (d) {
    case 'tech': return T.areaTech;
    case 'product': return T.areaProduct;
    case 'sales_gtm': return T.areaSales;
    case 'consulting': return T.areaConsulting;
    case 'marketing': return T.areaMarketing;
    case 'finance': return T.areaFinance;
    case 'academic': return T.areaAcademic;
    case 'general': return T.areaGeneral;
    default:
      // Humanise any newer recruiter-only domain (operations, skilled_trades,
      // customer_service, ...) so we never surface a raw snake_case slug.
      return String(d).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

function labelForType(t: string, T: typeof STRINGS_EN): string {
  switch (t) {
    case 'behavioral': return T.behav;
    case 'coding': return T.coding;
    case 'system_design': return T.systemDesign;
    case 'scenario': return T.scenario;
    case 'technical': return T.technical;
    default: return t;
  }
}


const STRINGS_EN = {
  heroTitle: 'AI Interview Simulations',
  /**
   * Embedded-mode hero headline rendered as:
   *   "Practice {privately} with AI Interview Simulations"
   * with the highlight word styled in a rose gradient. The `{}` braces
   * are stripped at render time; everything inside them gets the
   * gradient-text treatment.
   */
  heroTitleEmbedded: 'Practice with {AI Interview Simulations}',
  heroSub: 'AI video interview - private practice',
  heroBody:
    'Practice real interview formats in a full AI video interview - an AI interviewer that listens, follows your code and whiteboard live, and adapts. Afterwards you re-watch the recording and get structured feedback on what worked and what to refine.',
  history: 'History',
  filters: 'Filters',
  libraryToggleTitle: 'Cevee Trainings',
  libraryToggleSubtitle: 'Ready-made interview formats by role - browse, filter & start.',
  libraryInfoShort: 'How it works',
  libraryInfoTitle: 'AI Interview Simulations',
  libraryInfoLead: 'Realistic AI video interviews with replay and structured feedback. Practice is included with Explorer Premium (10 simulations / month). Passport Pre-Screens stay free.',
  libraryInfoFeaturesHeading: 'What you get',
  libraryInfoFeature1: 'Realistic AI interviewer that adapts live to your answers.',
  libraryInfoFeature2: 'Coding panel, whiteboard, notepad, numbers, depending on the format.',
  libraryInfoFeature3: 'Re-watch the recording and get private structured feedback.',
  libraryInfoFeature4: 'Private, never shared with recruiters. EU storage, no AI training.',
  libraryInfoHowHeading: 'How the library works',
  libraryInfoBullet1: 'Curated practice pools only. Each card is a themed interview format, not a one-off question list.',
  libraryInfoBullet2: 'Career level and length (30 / 45 / 60 min) you choose when you start a simulation.',
  libraryInfoBullet3: 'Real hiring applications use a separate hiring pool, so practice cases do not show up again in recruiter interviews.',
  libraryInfoClose: 'Got it',
  paywallTitle: 'Practice is Premium',
  paywallBody: 'AI interview simulations with replay and structured feedback are included with Explorer Premium.',
  paywallPrice: '€4.99 / month',
  paywallQuota: '10 simulations per month',
  paywallPrescreen: 'Passport Pre-Screens stay free on every plan.',
  paywallSubscribe: 'Subscribe to Premium',
  paywallSeePricing: 'See all plans',
  durationAtStart: '30 / 45 / 60 min at start',
  searchPlaceholder: 'Search templates ...',
  area: 'Area',
  areaTech: 'Tech',
  areaProduct: 'Product',
  areaSales: 'Sales / GTM',
  areaConsulting: 'Consulting',
  areaMarketing: 'Marketing',
  areaFinance: 'Finance',
  areaAcademic: 'Academic',
  areaGeneral: 'General',
  language: 'Language',
  type: 'Type',
  level: 'Level',
  lvlStudent: 'Student',
  all: 'All',
  behav: 'Behavioral',
  coding: 'Coding',
  systemDesign: 'System Design',
  scenario: 'Scenario',
  technical: 'Technical',
  startMock: 'Configure simulation',
  starting: 'Starting...',
  thisPeriod: 'This period',
  left: 'left',
  resetsOn: 'Resets on',
  tierFree: 'Free',
  tierPremium: 'Premium',
  tierStar: 'Star',
  panelCode: 'Coding panel',
  panelWb: 'Whiteboard',
  panelNumbers: 'Numbers',
  panelNotepad: 'Notepad',
  empty: 'No templates match these filters. Try clearing some.',
  pillScorecard: 'Structured feedback',
  pillReplay: 'Re-watch your recording',
  pillPrivate: 'Only visible to you',
  templateSingular: 'template',
  templatePlural: 'templates',
  viewDetails: 'Details',
  consentTitle: 'Start your AI video interview simulation?',
  consentLead: 'Pick settings, then confirm recording & privacy.',
  consentPersona: 'AI interviewer',
  consentSummary:
    'Audio, video and screen are recorded for playback and private AI feedback. EU storage (Azure France), 90 days, deletable, not used for AI training.',
  consentDetailsShow: 'Show all details',
  consentDetailsHide: 'Hide details',
  contextLabel: 'Anything to focus on? (optional)',
  contextPlaceholder:
    "e.g. 'Final-round tone' or 'Push more on structure and trade-offs' or 'I'm targeting senior PM roles'",
  contextHint:
    'Optional add-on. The template already defines the interview. Use this to steer tone or emphasis.',
  charsLeft: 'chars left',
  consentBullet1: 'Audio, video + screen are recorded so you can re-watch and get structured feedback.',
  consentBullet2: 'AI evaluation is generated for your eyes only - no recruiter sees any of this.',
  consentBullet3: 'Storage on EU infrastructure (Azure France). No use for AI training. 90-day retention, delete anytime.',
  consentBullet4: 'Cevee staff has technical access only for support, abuse review or legal requests - never for active observation.',
  consentCheckbox: 'I understand and agree to the recording + AI evaluation.',
  consentLegalRef: 'Details:',
  consentCancel: 'Cancel',
  consentConfirm: 'Start now',
  privacyTitle: 'Your simulations stay yours',
  privacyBody: 'Recordings and transcripts are encrypted, stored in the EU, and never used to train any AI model. No recruiter and no third party can access your simulations. Cevee staff may technically access data only for support, abuse review or legal requests, never for active observation. Fully GDPR-compliant.',
  privacyLink: 'Privacy',
  termsLink: 'Terms',
  avvLink: 'DPA',
  // Custom-Simulation CTA + builder modal
  customTitle: 'Build your own interview',
  customBody: 'Paste a problem, name a target company, or describe a scenario - your AI interviewer crafts a tailored video interview for it.',
  customCta: 'Build your own',
  customModalTitle: 'Design your own AI video interview',
  customModalLead: 'Tell the AI what to interview you on. Everything else - duration, persona, panels - is up to you.',
  customTopicLabel: 'Topic / problem / scenario',
  customTopicPlaceholder: "Paste the task or describe the upcoming interview - e.g. a coding problem you want to drill, the role and company you're prepping for, a behavioral focus, or a market-sizing scenario.",
  customTopicHint: 'Min. 20 chars. The more concrete, the better.',
  customType: 'Type',
  customLevel: 'Level',
  customDuration: 'Duration',
  customLanguage: 'Language',
  customPersona: 'AI interviewer',
  customPanels: 'Live panels (optional)',
  customPanelCoding: 'Coding editor',
  customPanelWB: 'Whiteboard',
  customPanelNumbers: 'Numbers',
  customPanelNotepad: 'Notepad',
  customPanelNotepadHint: 'Yellow post-it scratch surface for free-form notes',
  customStart: 'Configure simulation',
  screenShareNote: 'Simulations always record your screen alongside your webcam (composited into one video). You\'ll be asked to pick a window/screen in the next step.',
  typeBehav: 'Behavioral',
  typeCoding: 'Coding',
  typeSystemDesign: 'System Design',
  typeScenario: 'Scenario',
  typeTechnical: 'Technical',
};

const STRINGS_DE = {
  heroTitle: 'AI-Interview-Simulationen',
  heroTitleEmbedded: 'Übe mit {AI-Interview-Simulationen}',
  heroSub: 'AI-Video-Interview - privat zum Üben',
  heroBody:
    'Übe echte Interview-Formate in einer kompletten AI-Video-Interview-Simulation - mit einer KI-Interviewerin, die zuhört, deinen Code und das Whiteboard live mitverfolgt und sich anpasst. Danach kannst du dir die Aufnahme nochmal ansehen und bekommst strukturiertes Feedback dazu, was gut lief und was du beim nächsten Mal schärfer machen kannst.',
  history: 'Verlauf',
  filters: 'Filter',
  libraryToggleTitle: 'Cevee Trainings',
  libraryToggleSubtitle: 'Fertige Interview-Trainings nach Rolle - durchsuchen, filtern & starten.',
  libraryInfoShort: 'So funktioniert\'s',
  libraryInfoTitle: 'AI-Interview-Simulationen',
  libraryInfoLead: 'Realistische AI-Video-Interviews mit Replay und strukturiertem Feedback. Practice ist in Explorer Premium enthalten (10 Simulationen / Monat). Passport Pre-Screens bleiben kostenlos.',
  libraryInfoFeaturesHeading: 'Was du bekommst',
  libraryInfoFeature1: 'Realistische:r AI-Interviewer:in, passt sich live an deine Antworten an.',
  libraryInfoFeature2: 'Coding-Panel, Whiteboard, Notizblock, Numbers - je nach Format.',
  libraryInfoFeature3: 'Aufnahme nochmal anschauen + privates strukturiertes Feedback.',
  libraryInfoFeature4: 'Privat - nie mit Recruiter:innen geteilt. EU-Speicherung, kein KI-Training.',
  libraryInfoHowHeading: 'So funktioniert die Library',
  libraryInfoBullet1: 'Nur kuratierte Übungspools. Jede Karte ist ein Interview-Format, kein Einmal-Fragenkatalog.',
  libraryInfoBullet2: 'Karriere-Level und Dauer (30 / 45 / 60 Min.) wählst du beim Start jeder Simulation.',
  libraryInfoBullet3: 'Echte Bewerbungen nutzen einen getrennten Hiring-Pool, damit Übungs-Cases nicht in Interviews wiederkehren.',
  libraryInfoClose: 'Verstanden',
  paywallTitle: 'Practice ist Premium',
  paywallBody: 'AI-Interview-Simulationen mit Replay und strukturiertem Feedback sind in Explorer Premium enthalten.',
  paywallPrice: '€4,99 / Monat',
  paywallQuota: '10 Simulationen pro Monat',
  paywallPrescreen: 'Passport Pre-Screens bleiben auf jedem Tarif kostenlos.',
  paywallSubscribe: 'Premium abonnieren',
  paywallSeePricing: 'Alle Pläne ansehen',
  durationAtStart: '30 / 45 / 60 Min. beim Start',
  searchPlaceholder: 'Templates durchsuchen ...',
  area: 'Bereich',
  areaTech: 'Tech',
  areaProduct: 'Product',
  areaSales: 'Sales / GTM',
  areaConsulting: 'Consulting',
  areaMarketing: 'Marketing',
  areaFinance: 'Finance',
  areaAcademic: 'Forschung',
  areaGeneral: 'Allgemein',
  language: 'Sprache',
  type: 'Typ',
  level: 'Level',
  lvlStudent: 'Studi',
  all: 'Alle',
  behav: 'Verhalten',
  coding: 'Coding',
  systemDesign: 'System Design',
  scenario: 'Szenario',
  technical: 'Technisch',
  startMock: 'Simulation konfigurieren',
  starting: 'Starte...',
  thisPeriod: 'Diese Periode',
  left: 'übrig',
  resetsOn: 'Reset am',
  tierFree: 'Free',
  tierPremium: 'Premium',
  tierStar: 'Star',
  panelCode: 'Coding-Panel',
  panelWb: 'Whiteboard',
  panelNumbers: 'Zahlen',
  panelNotepad: 'Notizblock',
  empty: 'Keine Templates passen zu diesen Filtern. Versuch sie zu lockern.',
  pillScorecard: 'Strukturiertes Feedback',
  pillReplay: 'Video danach ansehen',
  pillPrivate: 'Nur für Dich sichtbar',
  templateSingular: 'Template',
  templatePlural: 'Templates',
  viewDetails: 'Details',
  consentTitle: 'AI-Video-Interview-Simulation starten?',
  consentLead: 'Einstellungen wählen, dann Aufnahme & Datenschutz bestätigen.',
  consentPersona: 'AI-Interviewerin',
  consentSummary:
    'Audio, Video und Bildschirm werden für Replay und private KI-Bewertung aufgezeichnet. EU (Azure Frankreich), 90 Tage, löschbar, kein KI-Training.',
  consentDetailsShow: 'Alle Details anzeigen',
  consentDetailsHide: 'Details ausblenden',
  contextLabel: 'Fokus für die KI (optional)',
  contextPlaceholder:
    "z.B. 'Final-Round-Niveau' oder 'Mehr auf Struktur und Trade-offs drücken' oder 'Ich will Richtung Senior PM'",
  contextHint:
    'Optional. Das Template legt das Interview schon fest. Hier nur Ton oder Schwerpunkt steuern.',
  charsLeft: 'Zeichen frei',
  consentBullet1: 'Audio, Video und Bildschirm werden aufgezeichnet, damit du das später nochmal ansehen kannst und strukturiertes Feedback bekommst.',
  consentBullet2: 'Die KI-Bewertung ist privat - keine Recruiter:in und keine Drittpartei sieht sie.',
  consentBullet3: 'Speicherung in der EU (Azure Frankreich). Keine KI-Trainingsnutzung. 90 Tage Aufbewahrung, jederzeit löschbar.',
  consentBullet4: 'Cevee-Mitarbeitende haben technischen Zugriff nur für Support, Missbrauchsprüfung oder behördliche Anfragen - nie zur aktiven Beobachtung.',
  consentCheckbox: 'Ich verstehe das und stimme der Aufzeichnung + KI-Bewertung zu.',
  consentLegalRef: 'Details:',
  consentCancel: 'Abbrechen',
  consentConfirm: 'Jetzt starten',
  privacyTitle: 'Deine Simulationen bleiben deine',
  privacyBody: 'Aufnahmen und Transkripte sind verschlüsselt, werden in der EU gespeichert und niemals für KI-Training verwendet. Keine Recruiter:in und keine Drittpartei kann auf deine Simulationen zugreifen. Cevee-Mitarbeitende können technisch nur im Rahmen von Support, Missbrauchsprüfung oder behördlichen Anfragen Zugriff erhalten - nie zur aktiven Beobachtung. DSGVO-konform.',
  privacyLink: 'Datenschutz',
  termsLink: 'AGB',
  avvLink: 'AVV',
  // Custom-Simulation CTA + builder modal
  customTitle: 'Eigenes Interview bauen',
  customBody: 'Paste eine Aufgabe rein, nenn ein Zielunternehmen oder beschreib ein Szenario - deine KI-Interviewerin baut dir ein passgenaues Video-Interview drumherum.',
  customCta: 'Eigenes Interview',
  customModalTitle: 'Eigenes AI-Video-Interview entwerfen',
  customModalLead: 'Sag der KI, worauf du interviewt werden willst. Den Rest - Dauer, Persona, Panels - bestimmst du.',
  customTopicLabel: 'Thema / Aufgabe / Szenario',
  customTopicPlaceholder: "Paste die Aufgabe rein oder beschreib das bevorstehende Interview - z.B. eine Coding-Aufgabe zum Üben, Rolle und Firma auf die du dich vorbereitest, ein Behavioral-Fokus oder ein Market-Sizing-Szenario.",
  customTopicHint: 'Min. 20 Zeichen. Je konkreter, desto besser.',
  customType: 'Typ',
  customLevel: 'Level',
  customDuration: 'Dauer',
  customLanguage: 'Sprache',
  customPersona: 'KI-Interviewer:in',
  customPanels: 'Live-Panels (optional)',
  customPanelCoding: 'Coding-Editor',
  customPanelWB: 'Whiteboard',
  customPanelNumbers: 'Zahlen',
  customPanelNotepad: 'Notizblock',
  customPanelNotepadHint: 'Gelber Stift-Bereich für freie Notizen',
  screenShareNote: 'Simulationen zeichnen immer deinen Bildschirm zusätzlich zur Webcam auf (kombiniert in einem Video). Du wirst im nächsten Schritt einen Bildschirm oder ein Fenster auswählen.',
  customStart: 'Simulation konfigurieren',
  typeBehav: 'Verhalten',
  typeCoding: 'Coding',
  typeSystemDesign: 'System Design',
  typeScenario: 'Szenario',
  typeTechnical: 'Technisch',
};


export default MockHome;
